import { useCallback, useEffect, useState, type JSX } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Spread } from '../shared/components/Spread.js';
import { ChapbookDivider } from '../shared/components/ChapbookDivider.js';
import { CurtainCast } from './components/CurtainCast.js';
import { GameEndBurnCanvas } from './GameEndBurnCanvas.js';
import { GameEndClose } from './GameEndClose.js';
import { GhostVoidScene } from './GhostVoidScene.js';
import { RoleRevealVerso, RoleRevealRecto } from '../discussion/RoleReveal.js';
import { GuessRevealVerso, GuessRevealRecto } from '../discussion/GuessReveal.js';
import type { RoleRevealView, GuessRevealView } from '../discussion/types.js';
import type { GameEndView } from './types.js';
import './GameEnd.css';

/**
 * The RoleReveal step value that represents the fully-resolved state
 * (struck cells faded, RevealPlate visible). See RoleReveal.tsx — the
 * step machine is 0 → 1 → 2 on a clean live arrival. GameEnd's
 * cinematic underlay always mounts at the resolved step because the
 * close fires AFTER RoleReveal has finished its choreography.
 */
const RESOLVED_REVEAL_STEP = 2 as const;

type FrozenPriorPhase =
  | { phase: 'role-reveal'; view: RoleRevealView }
  | { phase: 'guess-reveal'; view: GuessRevealView };

interface GameEndProps {
  view: GameEndView;
  /** Host-only: fires the Back-to-Lobby reset. The caller (App.tsx) decorates
   *  this with the GameEndResetFade wash so the visible click lands at peak
   *  cover rather than into a flash of the live room. */
  onBackToLobby: () => void;
  /** Book outcomes only: play the close (village) / burn (ghosts) cinematic on entry. */
  transitioning?: boolean;
  /** Fires when the cinematic settles. */
  onTransitionComplete?: () => void;
  /** Frozen snapshot of the prior phase's view, used to render the cinematic
   *  underlay (verso + closing-leaf recto) so the user sees the same pixels
   *  they were looking at one frame before the close fired. Null/undefined =
   *  AFK arrival (no meaningful prior content); underlay renders blank paper. */
  frozenPriorPhase?: FrozenPriorPhase | null;
}

/**
 * Renders the prior phase's verso content during the close/burn cinematic.
 * Switches on the frozen phase to mount the real reveal-arc component in
 * its resolved state (step=2 for RoleReveal), so the underlay shows the same
 * pixels the user was looking at one frame before the cinematic fired.
 *
 * Returns null when frozen is null/undefined — the AFK case where
 * the player wasn't actively engaged with the prior phase (Discussion or
 * ClueGiving → 0ms RoleReveal → GameEnd transit; the recto wasn't painted).
 */
function PriorPhaseVerso({ frozen }: { frozen: FrozenPriorPhase | null | undefined }): JSX.Element | null {
  if (frozen == null) return null;
  if (frozen.phase === 'role-reveal') return <RoleRevealVerso view={frozen.view} step={RESOLVED_REVEAL_STEP} />;
  return <GuessRevealVerso view={frozen.view} />;
}

/** Mirror of PriorPhaseVerso for the closing leaf's recto face. */
function PriorPhaseRecto({ frozen }: { frozen: FrozenPriorPhase | null | undefined }): JSX.Element | null {
  if (frozen == null) return null;
  if (frozen.phase === 'role-reveal') return <RoleRevealRecto view={frozen.view} step={RESOLVED_REVEAL_STEP} />;
  return <GuessRevealRecto view={frozen.view} />;
}

/**
 * GameEnd — the terminal phase (the curtain call). A pure projection of
 * terminal state — it is reached from six paths (some AFK passthroughs skip
 * every pre-beat), so it renders entirely from `view` and never assumes a
 * preceding cinematic.
 *
 * - Village win → warm baseline, the book closes.
 * - Ghost win  → cool Spectral Drench, the book is consumed (the void takes the room).
 * - Stalemate  → Lights-Out inversion (everyone lost), full-bleed, no book.
 */
export function GameEnd({ view, onBackToLobby, transitioning, onTransitionComplete, frozenPriorPhase }: GameEndProps): JSX.Element {
  // Thin dispatcher (no hooks) → each outcome is its own surface, since their
  // topologies genuinely differ: village = the book CLOSES to a single page;
  // ghosts = the book BURNS to a cool scorched ground; stalemate = Lights-Out.
  if (view.side === 'none') return <GameEndLightsOut view={view} onBackToLobby={onBackToLobby} />;
  if (view.side === 'village') {
    return (
      <GameEndVillage
        view={view}
        onBackToLobby={onBackToLobby}
        {...(transitioning !== undefined ? { transitioning } : {})}
        {...(onTransitionComplete !== undefined ? { onTransitionComplete } : {})}
        {...(frozenPriorPhase !== undefined ? { frozenPriorPhase } : {})}
      />
    );
  }
  return (
    <GameEndGhost
      view={view}
      onBackToLobby={onBackToLobby}
      {...(transitioning !== undefined ? { transitioning } : {})}
      {...(onTransitionComplete !== undefined ? { onTransitionComplete } : {})}
      {...(frozenPriorPhase !== undefined ? { frozenPriorPhase } : {})}
    />
  );
}

/** The resting closed-book page — the chapbook's final printed page, holding
 *  the full unmask the players came for: a verdict headline at the top of the
 *  page, the curtain call as the principal payload (the cast is the hero —
 *  brief §0.1), then a coda foot with the guess/word answer and the host
 *  action. Tighter than `GameEndResult` (no divider, no duplicated headers) so
 *  the printed content honours the single-page 410:660 aspect cap rather than
 *  overflowing the closed book and poking up above the close-cinematic overlay. */
function VillageRestingPage({ view, onBackToLobby }: GameEndProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="susdle-gameend-page">
      <h2 className="susdle-gameend-result__verdict">{t('gameEnd.verdict.village')}</h2>
      <p className="susdle-gameend-result__reason">{t(`gameEnd.reason.${view.outcome}`)}</p>
      <CurtainCast view={view} />
      <div className="susdle-gameend-page__coda">
        {view.word.shown ? (
          <p className="susdle-gameend-result__word">
            <Trans
              i18nKey="gameEnd.word.shown"
              values={{ word: view.word.word.toUpperCase() }}
              components={[<span className="susdle-gameend-word" key="w" />]}
            />
          </p>
        ) : null}
        <GameEndAction view={view} onBackToLobby={onBackToLobby} />
      </div>
    </div>
  );
}

/**
 * Village win — the book CLOSES.
 *
 * ONE Spread element throughout: the shared `<Spread closed>` (single 410:660
 * page = exactly one page of the open spread) carrying the full unmask is THE
 * book, the same book the prior phase was on, period — no second Spread is
 * ever rendered for the cinematic. The close gesture is just `GameEndClose`,
 * a single folding-leaf overlay rendered as this Spread's `overlay` child and
 * positioned `left: 100%` — sitting immediately to the right of the closed
 * page. At `rotateY: 0` the eye reads "closed page on the left + recto leaf on
 * the right = an open spread, this very book one moment before closing"; the
 * leaf then rotates 0°→-180° around its left edge (the spine of that illusion)
 * onto the closed page and fades, revealing the resting book underneath.
 *
 * GameEnd builds this from its own view-model — it does NOT consume the prior
 * phase's DOM — so the gesture works on every path (incl. the AFK 0ms paths,
 * brief §0.1). On cold arrival / reduced motion the leaf is skipped (its own
 * onComplete-first bail), so only the resting closed book renders.
 */
function GameEndVillage({ view, onBackToLobby, transitioning, onTransitionComplete, frozenPriorPhase }: GameEndProps): JSX.Element {
  const { t } = useTranslation();
  /* Two stages of "the close happened," timed independently so the GameEnd
     reveal emerges THROUGH the leaf's fade rather than popping in after:
       - `transitioning` (parent-owned) — the leaf is on screen, fold + fade in
         flight. When false the leaf is unmounted.
       - `revealSettled` (local) — the leaf has rotated to -180° and the verso
         underneath can swap from the prior-phase snapshot to the GameEnd
         reveal. Fires on `onLeafSettled` from GameEndClose, ~150ms BEFORE the
         leaf finishes fading. The opaque leaf back is still on top at that
         instant, hiding the verso swap; as the leaf then fades, the reveal is
         what emerges — no clean post-close window where the prior-phase verso
         lingers (the "flash" otherwise visible at ~t=1.2s).
     On cold arrival (`transitioning === false` from mount) the reveal is
     visible immediately — no leaf gesture played. */
  const [revealSettled, setRevealSettled] = useState(!transitioning);
  useEffect(() => {
    if (!transitioning) setRevealSettled(true);
    else setRevealSettled(false);
  }, [transitioning]);

  return (
    <div
      className="susdle-gameend susdle-gameend--village"
      data-side="village"
      data-closing={transitioning ? 'yes' : 'no'}
    >
      <h1 className="susdle-sr-only">{t('gameEnd.ariaLabel')}</h1>
      <Spread
        closed
        pov="village"
        ariaLabel={t('gameEnd.ariaLabel')}
        verso={
          revealSettled
            /* The reveal — full unmask, verdict, word, host action. Mounted
               at `onLeafSettled` time (before the leaf finishes fading) so it
               emerges through the fade rather than popping in after. */
            ? <VillageRestingPage view={view} onBackToLobby={onBackToLobby} />
            /* The PRIOR phase's verso (cast in 3-col grid, WITHOUT role labels)
               — what was on the spread the instant before the close fired. */
            : <PriorPhaseVerso frozen={frozenPriorPhase} />
        }
      />
      {/* The folding leaf hosts as a SIBLING of the closed Spread, not its
          overlay child, because the spread's aspect-lock requires
          `overflow: hidden`, which would clip the leaf if it were inside.
          The host is positioned to coincide with where the closed Spread is
          (same width formula, same vertical centering) so the leaf — rendered
          internally at `left: 100%` of its host — lands exactly at the spread's
          right edge (the spine of the open-illusion), folding onto the spread
          from outside its clipping context. */}
      {transitioning ? (
        <div className="susdle-gameend-close-host" aria-hidden>
          <GameEndClose
            rectoContent={<PriorPhaseRecto frozen={frozenPriorPhase} />}
            onLeafSettled={() => setRevealSettled(true)}
            onComplete={onTransitionComplete ?? (() => undefined)}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Ghost win — the book is CONSUMED.
 *
 * Same ONE-BOOK / content-agnostic / reveal-after-gesture pattern as the
 * villager close, but the resting state is the spectral VOID (`GhostVoidScene`)
 * rather than a closed book — the book burns away entirely. During the
 * cinematic (`transitioning === true`) the `GameEndBurn` overlay carries the
 * prior phase's open spread and consumes it in cool Spectral flames. At
 * `onConsumed` time the book is gone; the void underneath is mounted under
 * the still-fading burn overlay so the reveal emerges through the
 * dissipation rather than popping in after.
 *
 * Reduced motion + cold arrival both skip straight to the void.
 */
function GameEndGhost({ view, onBackToLobby, transitioning, onTransitionComplete, frozenPriorPhase }: GameEndProps): JSX.Element {
  const { t } = useTranslation();
  const [voidSettled, setVoidSettled] = useState(!transitioning);
  useEffect(() => {
    if (!transitioning) setVoidSettled(true);
    else setVoidSettled(false);
  }, [transitioning]);

  /* Stable callback identities — if these were inline arrows, the first
     onConsumed call (which sets voidSettled and triggers a re-render) would
     create NEW prop identities on the burn canvas, which would then re-run
     its mount effect mid-cinematic and fire a second emission wave on top
     of the first. The canvas internally also stabilises against this via
     refs, but stabilising at the source is the defence-in-depth fix. */
  const handleConsumed = useCallback(() => { setVoidSettled(true); }, []);
  const handleComplete = useCallback(
    () => { onTransitionComplete?.(); },
    [onTransitionComplete],
  );

  return (
    <div
      className="susdle-gameend susdle-gameend--ghosts"
      data-side="ghosts"
      data-closing={transitioning ? 'yes' : 'no'}
    >
      <h1 className="susdle-sr-only">{t('gameEnd.ariaLabel')}</h1>
      {voidSettled ? (
        <GhostVoidScene view={view} onBackToLobby={onBackToLobby} />
      ) : null}
      {transitioning ? (
        <GameEndBurnCanvas
          versoContent={<PriorPhaseVerso frozen={frozenPriorPhase} />}
          rectoContent={<PriorPhaseRecto frozen={frozenPriorPhase} />}
          onConsumed={handleConsumed}
          onComplete={handleComplete}
        />
      ) : null}
    </div>
  );
}

/** Host: the Back-to-Lobby control (Begin treatment + Oxblood seed-dot). Non-host: a waiting line. */
function GameEndAction({ view, onBackToLobby }: GameEndProps): JSX.Element {
  const { t } = useTranslation();
  if (!view.isHost) {
    return (
      <p className="susdle-gameend-waiting">{t('gameEnd.action.waiting', { host: view.hostName })}</p>
    );
  }
  return (
    <div className="susdle-gameend-action">
      <ChapbookDivider />
      <button
        type="button"
        className="susdle-action susdle-action--prominent susdle-gameend-begin"
        data-voice="display"
        onClick={onBackToLobby}
      >
        {t('gameEnd.action.begin')}
      </button>
    </div>
  );
}

/**
 * Stalemate — the Lights-Out inversion (everyone lost). Full-bleed warm-ink
 * ground with cream linework, the watching eyes, and the full cast unmasked as a
 * quiet shared-defeat roster (no portraits: the lights-out is outside the warm
 * book, like the ClosedDoor). Accent-free; the inversion is the verdict.
 */
function GameEndLightsOut({ view, onBackToLobby }: GameEndProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <section className="susdle-gameend-lightsout" aria-label={t('gameEnd.ariaLabel')}>
      <div className="susdle-gameend-lightsout__inner">
        {/* Rough/mottled woodcut eyes (the rough + mottle filters mirror the
            ClosedDoor EyesSigil) so the stare reads as a folk-horror engraving,
            never a smooth/googly cartoon. Size + placement mirrors the
            ClosedDoor (min(768px, 60vw) inside a flex-centered wrap) so the
            two surfaces feel like cousins from the same iconography family.
            FOLLOW-UP: extract a shared EyesSigil. */}
        <div className="susdle-gameend-eyes-wrap" aria-hidden>
          <svg className="susdle-gameend-eyes" viewBox="0 24 360 82" aria-hidden role="presentation" focusable={false}>
            <defs>
              <filter id="susdle-ge-rough" x="-25%" y="-25%" width="150%" height="150%">
                <feTurbulence type="fractalNoise" baseFrequency="0.08" numOctaves="3" seed="11" result="n" />
                <feDisplacementMap in="SourceGraphic" in2="n" scale="7" xChannelSelector="R" yChannelSelector="G" />
              </filter>
              <filter id="susdle-ge-mottle" x="-10%" y="-10%" width="120%" height="120%">
                <feTurbulence type="fractalNoise" baseFrequency="0.5" numOctaves="2" seed="5" result="g" />
                <feColorMatrix in="g" type="matrix" values="0 0 0 0 0.12  0 0 0 0 0.10  0 0 0 0 0.08  0 0 0 0.4 0" result="speck" />
                <feComposite in="speck" in2="SourceGraphic" operator="in" result="clip" />
                <feMerge><feMergeNode in="SourceGraphic" /><feMergeNode in="clip" /></feMerge>
              </filter>
            </defs>
            <g filter="url(#susdle-ge-rough)">
              <g transform="rotate(-10 80 65)">
                <path className="sclera" filter="url(#susdle-ge-mottle)" d="M 35 65 C 39.4 48.3 56.8 38.6 80 40 C 103.2 41.4 120.6 51.1 125 65 C 120.6 81.7 100.3 91.4 78.55 90 C 56.8 88.6 39.4 81.7 35 65 Z" />
                <circle className="pupil" cx="80" cy="65" r="9" />
              </g>
              <g transform="rotate(10 280 65)">
                <path className="sclera" filter="url(#susdle-ge-mottle)" d="M 325 65 C 320.6 48.3 303.2 38.6 280 40 C 256.8 41.4 239.4 51.1 235 65 C 239.4 81.7 259.7 91.4 281.45 90 C 303.2 88.6 320.6 81.7 325 65 Z" />
                <circle className="pupil" cx="280" cy="65" r="9" />
              </g>
            </g>
          </svg>
        </div>

        <h1 className="susdle-gameend-lightsout__title">{t('gameEnd.verdict.stalemate')}</h1>
        <p className="susdle-gameend-lightsout__reason">{t('gameEnd.reason.stalemate')}</p>

        <ul className="susdle-gameend-roster" aria-label={t('gameEnd.curtain.allAria')}>
          {view.cast.map((p) => (
            <li key={p.id} className="susdle-gameend-roster__row">
              <span className="susdle-gameend-roster__name">{p.name}</span>
              <span className="susdle-gameend-roster__role">{t(`gameEnd.role.${p.role}`)}</span>
            </li>
          ))}
        </ul>

        {view.word.shown ? (
          <p className="susdle-gameend-lightsout__word">
            <Trans
              i18nKey="gameEnd.word.shown"
              values={{ word: view.word.word.toUpperCase() }}
              components={[<span className="susdle-gameend-word" key="w" />]}
            />
          </p>
        ) : null}

        {view.isHost ? (
          <button
            type="button"
            className="susdle-action susdle-action--prominent susdle-gameend-begin susdle-gameend-begin--lightsout"
            data-voice="display"
            data-tone="lightsout"
            onClick={onBackToLobby}
          >
            {t('gameEnd.action.begin')}
          </button>
        ) : (
          <p className="susdle-gameend-waiting susdle-gameend-waiting--lightsout">
            {t('gameEnd.action.waiting', { host: view.hostName })}
          </p>
        )}
      </div>
    </section>
  );
}
